import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { AssetCard } from "@/components/assets/asset-card";
import {
  QrCode,
  Package,
  Search,
  ArrowRight,
  LogIn,
  AlertTriangle,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/assets/")({
  component: AssetListPage,
});

interface Asset {
  id: string;
  name: string;
  assetType: string;
  serialNumber: string | null;
  tagNumber: string | null;
  location: string | null;
  status: string;
  isActive: boolean;
  imageFileId: string | null;
  lastInspectedAt: string | null;
  nextInspectionDueAt: string | null;
  inspectionTemplateName?: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Assignment {
  assignment: {
    id: string;
    assetId: string;
    userId: string;
    checkedOutAt: string;
    checkedInAt: string | null;
    conditionOut: string;
  };
  assetName: string;
  assetType: string;
  assetStatus: string;
  serialNumber: string | null;
  tagNumber: string | null;
  location: string | null;
  imageFileId: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  available: "bg-green-100 text-green-800",
  checked_out: "bg-yellow-100 text-yellow-800",
  out_of_service: "bg-red-100 text-red-800",
  retired: "bg-gray-100 text-gray-600",
};

const STATUS_LABELS: Record<string, string> = {
  available: "Available",
  checked_out: "Checked Out",
  out_of_service: "Out of Service",
  retired: "Retired",
};

function AssetListPage() {
  const navigate = useNavigate();
  const { user } = Route.useRouteContext() as {
    user: { id: string; role?: string };
  };
  const [search, setSearch] = useState("");
  const [manualId, setManualId] = useState("");

  const canManageAssets = ["admin", "div_admin", "office_personnel"].includes(
    user?.role || ""
  );

  // Fetch worker's currently checked-out assets
  const { data: checkoutsData, isLoading: checkoutsLoading } = useQuery({
    queryKey: ["my-checkouts"],
    queryFn: () => apiGet<{ data: Assignment[] }>("/api/assets/my-checkouts"),
  });

  // Fetch available assets
  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ["assets", search],
    queryFn: () =>
      apiGet<{ data: Asset[]; total: number }>(
        `/api/assets?status=available&search=${encodeURIComponent(search)}&limit=20`
      ),
  });

  // Overdue inspections -- only for foreman/manager/admin
  const { data: overdueData } = useQuery({
    queryKey: ["admin", "assets", "overdue-inspections"],
    queryFn: () =>
      apiGet<{ data: { id: string; name: string; daysOverdue: number }[] }>(
        "/api/admin/assets/overdue-inspections"
      ),
    enabled: canManageAssets,
  });
  const overdueCount = overdueData?.data?.length ?? 0;

  const myCheckouts = checkoutsData?.data || [];
  const assets = assetsData?.data || [];

  const handleManualSearch = () => {
    const id = manualId.trim();
    if (id) {
      navigate({ to: "/assets/$assetId", params: { assetId: id } });
    }
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      {/* Header with Scan button */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold text-text-primary">Assets</h1>
          {canManageAssets && overdueCount > 0 && (
            <Badge className="bg-amber-500 text-white text-xs px-1.5 py-0 gap-1">
              <AlertTriangle className="h-3 w-3" />
              {overdueCount} overdue
            </Badge>
          )}
        </div>
        <Link to="/assets/scan">
          <Button className="bg-paris-red hover:bg-paris-red-dark gap-2">
            <QrCode className="h-4 w-4" />
            Scan QR
          </Button>
        </Link>
      </div>

      {/* My Checkouts section */}
      {checkoutsLoading && (
        <div className="space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      )}

      {!checkoutsLoading && myCheckouts.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            My Checkouts
          </h2>
          {myCheckouts.map((checkout) => (
            <Card
              key={checkout.assignment.id}
              className="border-yellow-200 bg-yellow-50/50"
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {checkout.assetName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Since{" "}
                      {new Date(checkout.assignment.checkedOutAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Link
                    to="/assets/$assetId"
                    params={{ assetId: checkout.assignment.assetId }}
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 shrink-0"
                    >
                      <LogIn className="h-3.5 w-3.5" />
                      Check In
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Manual Asset ID entry */}
      <div className="flex gap-2">
        <Input
          placeholder="Enter asset ID manually..."
          value={manualId}
          onChange={(e) => setManualId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleManualSearch()}
          className="flex-1"
        />
        <Button
          variant="outline"
          size="icon"
          onClick={handleManualSearch}
          disabled={!manualId.trim()}
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search assets by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Available assets list */}
      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Available Assets
        </h2>

        {assetsLoading && (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        )}

        {!assetsLoading && assets.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-10 w-10 text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">
                {search ? "No assets match your search" : "No available assets"}
              </p>
            </CardContent>
          </Card>
        )}

        {!assetsLoading &&
          assets.map((asset) => (
            <Link
              key={asset.id}
              to="/assets/$assetId"
              params={{ assetId: asset.id }}
              className="block"
            >
              <Card className="hover:bg-muted/30 transition-colors cursor-pointer">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {asset.name}
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge
                          variant="outline"
                          className="text-xs px-1.5 py-0"
                        >
                          {asset.assetType.replace("_", " ")}
                        </Badge>
                        <Badge
                          className={cn(
                            "text-xs px-1.5 py-0",
                            STATUS_COLORS[asset.status]
                          )}
                        >
                          {STATUS_LABELS[asset.status] || asset.status}
                        </Badge>
                      </div>
                      {asset.location && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {asset.location}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
      </div>
    </div>
  );
}
